import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Modal from '../components/Modal';
import CompanyChip from '../components/asset-company/CompanyChip';
import { useAssetMeta, deviceName, tagText } from '../lib/assets';
import '../components/Assets.css';
import BackLink from '../components/asset/BackLink';
import { cameFrom } from '../components/asset/BackLink';

/**
 * Devices: every machine with a Jarvis agent, in one list.
 *
 * The register says who HAS a device; the agent says who is AT it. Until now
 * that was visible one device at a time, on its page, and as three filters
 * on the register. This page is the two side by side for every reporting
 * machine - with (holder) and signed in - so a laptop that changed hands
 * without anybody telling IT shows up as a row, not as a surprise during a
 * ticket.
 *
 * One action, and it is explicit: "Make <name> the holder" gives the device
 * to the person signed in, through the same assign endpoint as the device
 * page (its own line in the history). Nothing here happens on its own; the
 * agent never changes a holder.
 *
 * The server decides who sees what: the rows are the devices this person's
 * grants reach, and each says whether they may change it. Contractors are
 * never here (who is signed in to the firm's computers is the firm's
 * business); App.js does not route them.
 *
 * The filter and search live in the address, as on the register, so a
 * narrowed list can be sent to a colleague.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const FILTERS = [
  ['all', 'All'], ['stale', 'Not reporting'], ['holder_differs', 'Holder ≠ signed in'],
  ['unencrypted', 'Not encrypted'], ['low_disk', 'Low disk'], ['lost', 'Lost'], ['incomplete', 'To complete'],
  // The server's one rule (lib/device-power.js), as on Fleet health: each
  // row it flags says why (patching_reasons).
  ['needs_patching', 'Needs patching']
];

const ago = (when) => {
  if (!when) return 'never';
  const mins = Math.round((Date.now() - new Date(when).getTime()) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 36) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const encryptionTone = (s) => (s === 'on' ? 'success' : s === 'off' ? 'critical' : 'idle');
const encryptionWord = (s) => (s === 'on' ? 'Encrypted' : s === 'off' ? 'Not encrypted' : 'Not known');
const diskTone = (pct) => (pct === null || pct === undefined ? 'idle' : pct >= 90 ? 'critical' : pct >= 80 ? 'warning' : 'idle');

/** Who the agent says is at the machine, in one phrase. */
const signedInWord = (d) => {
  const s = d.signed_in;
  if (!s) return null;
  return s.name || s.upn || s.account;
};

export default function AssetDevices() {
  const navigate = useNavigate();
  const meta = useAssetMeta();
  const [params, setParams] = useSearchParams();
  // The address as it is this moment. The router's copy (params) is updated
  // in a transition, so a click just made may not be in it yet; a write built
  // on it - the search written a moment after typing, above all - put back a
  // filter just switched off. Reading is from params; writing starts here.
  const liveParams = () => new URLSearchParams(window.location.search);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [query, setQuery] = useState(params.get('q') || '');
  const [picked, setPicked] = useState(() => new Set());
  // The confirm dialog: one device, or every ticked one it applies to.
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const show = FILTERS.some(([k]) => k === params.get('show')) ? params.get('show') : 'all';
  const setParam = (k, v) => {
    const next = liveParams();
    if (v && v !== 'all') next.set(k, v); else next.delete(k);
    setParams(next);
  };

  // Only the latest request's answer is shown (an older one arriving last
  // would put back the rows of a search already changed).
  const latest = useRef(0);
  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (show !== 'all') q.set('show', show);
    if (params.get('q')) q.set('q', params.get('q'));
    const n = ++latest.current;
    axios.get(`${API_BASE}/assets/devices?${q}`)
      .then((r) => { if (n === latest.current) { setData(r.data); setError(''); } })
      .catch((e) => { if (n === latest.current) setError(e.response?.data?.error || 'Could not load the devices.'); });
  }, [show, params]);
  useEffect(() => { load(); }, [load]);

  // Typing filters after a pause, rather than one request per key.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((liveParams().get('q') || '') !== query.trim()) setParam('q', query.trim());
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const devices = useMemo(() => data?.devices || [], [data]);
  const counts = data?.counts || {};
  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // The ticked rows the action applies to: a known person at the machine
  // who is not its holder, on a device this person may change.
  const applicable = useMemo(() => devices.filter((d) => picked.has(d.id) && d.can_make_holder), [devices, picked]);

  const makeHolders = async (list) => {
    setBusy(true);
    setError('');
    const done = [];
    const failed = [];
    for (const d of list) {
      try {
        await axios.post(`${API_BASE}/assets/${d.id}/assign`,
          { user_id: d.signed_in.user_id, reason: 'They are signed in to it, by its agent’s report' });
        done.push(d);
      } catch (e) {
        failed.push(`${tagText(d)}: ${e.response?.data?.error || 'that did not work'}`);
      }
    }
    setBusy(false);
    setConfirm(null);
    setPicked(new Set());
    if (done.length === 1) setNote(`${tagText(done[0])} is now with ${done[0].signed_in.name}.`);
    else if (done.length > 1) setNote(`${done.length} devices now have the person signed in as their holder.`);
    if (failed.length) setError(failed.join(' · '));
    load();
  };

  if (meta?.missing) {
    return (
      <div className="container">
        <p><BackLink /></p>
        <p className="as-note" data-tone="warning">{meta.error}</p>
      </div>
    );
  }

  return (
    <div className="container">
      <p><BackLink /></p>
      <div className="page-header">
        <h1 className="page-title">Devices</h1>
        <p className="page-description">
          Every computer with a Jarvis agent: who the register says has it, who is signed in to it, and
          what it last reported. Nothing here changes on its own — giving a device to the person at it is
          a button, and is written into its history.
          {' '}<Link to="/assets/devices/fleet" data-testid="fleet-link">Fleet health</Link> ·
          {' '}<Link to="/assets/discovery/cleanup">Things these computers linked automatically</Link>
        </p>
      </div>

      {error && !data && <p className="as-note" data-tone="warning" role="alert">{error}</p>}
      {data && (
        <>
          <div className="stg">
            <div className="stg-row" role="tablist" aria-label="Show">
              {FILTERS.map(([k, label]) => (
                <button key={k} type="button" role="tab" className="stg-tab" data-on={String(show === k)}
                  aria-selected={show === k} onClick={() => setParam('show', k)} data-filter={k}>
                  {label} <span className="stg-count">{counts[k] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="as-toolbar">
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Tag, model, hostname, holder or who is signed in" aria-label="Search the devices" />
            <span className="as-spacer" />
            <span className="as-quiet as-dev-legend">
              {`Not reporting after ${data.offline_days} day${data.offline_days === 1 ? '' : 's'} · who signed in needs agent ${data.sign_in_agent}`}
            </span>
          </div>

          {error && <p className="as-error" role="alert">{error}</p>}
          {note && <p className="as-note" data-tone="info" role="status">{note}</p>}

          {picked.size > 0 && (
            <div className="as-bulk" data-testid="bulk-holders">
              <strong>{`${picked.size} ticked`}</strong>
              <span className="as-quiet">
                {applicable.length === 0
                  ? 'None of these has a known person signed in who is not already its holder.'
                  : `${applicable.length} ${applicable.length === 1 ? 'has' : 'have'} somebody else signed in.`}
              </span>
              <button type="button" className="btn btn-primary btn-small" disabled={applicable.length === 0}
                onClick={() => setConfirm(applicable)}>
                Make signed-in person the holder
              </button>
              <span className="as-spacer" />
              <button type="button" className="btn btn-plain btn-small" onClick={() => setPicked(new Set())}>Untick all</button>
            </div>
          )}

          {devices.length === 0 ? (
            <div className="empty-state">
              <p>{data.total === 0
                ? 'No device has an agent yet. Install one from Settings → Assets & devices → Enrolment.'
                : 'No device matches.'}</p>
            </div>
          ) : (
            <div className="as-table-wrap">
              <table className="as-table as-devices">
                <thead>
                  <tr>
                    <th aria-label="Choose" />
                    <th>Device</th><th>With</th><th>Signed in</th><th>Last seen</th>
                    <th>OS</th><th>Encryption</th><th>Disk</th><th>Agent</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <DeviceRow key={d.id} d={d} picked={picked.has(d.id)} onPick={() => toggle(d.id)}
                      onOpen={() => navigate(`/assets/${d.id}`, { state: cameFrom('Devices') })} onMakeHolder={() => setConfirm([d])} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {confirm && (
        <Modal title={confirm.length === 1 ? `Make ${confirm[0].signed_in.name} the holder?` : 'Make the signed-in people the holders?'}
          onClose={() => setConfirm(null)}
          footer={(
            <>
              <button type="button" className="btn btn-plain" onClick={() => setConfirm(null)} disabled={busy}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => makeHolders(confirm)} disabled={busy}>
                {confirm.length === 1 ? `Make ${confirm[0].signed_in.name} the holder` : `Change ${confirm.length} holders`}
              </button>
            </>
          )}>
          <p>
            {confirm.length === 1
              ? `${tagText(confirm[0])} (${deviceName(confirm[0])}) is ${confirm[0].holder_name ? `with ${confirm[0].holder_name}` : 'with nobody'} in the register; its agent says ${confirm[0].signed_in.name} is signed in.`
              : 'Each device goes to the person its agent says is signed in:'}
          </p>
          {confirm.length > 1 && (
            <ul className="as-tree" data-testid="bulk-changes">
              {confirm.map((d) => (
                <li key={d.id}>
                  <strong>{tagText(d)}</strong>
                  <span>{`${d.holder_name || 'nobody'} → ${d.signed_in.name}`}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="as-quiet">
            Each change is written into the device’s history with your name. A device in stock becomes in use.
          </p>
        </Modal>
      )}
    </div>
  );
}

/** One device. At phone width the cells stack into a card (Assets.css). */
function DeviceRow({ d, picked, onPick, onOpen, onMakeHolder }) {
  const who = signedInWord(d);
  const f = d.flags;
  return (
    <tr onClick={onOpen} data-stale={String(f.stale)} data-id={d.id}>
      <td onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" className="as-select" checked={picked} onChange={onPick}
          aria-label={`Choose ${tagText(d)}`} />
      </td>
      <td data-label="Device">
        <a href={`/assets/${d.id}`} className="as-tag" onClick={(e) => { e.preventDefault(); onOpen(); }}>{tagText(d)}</a>
        <span className="as-sub">
          {[deviceName(d), d.hostname, d.company_name ? null : d.location_path].filter(Boolean).join(' · ')}
        </span>
        {d.company_name && (
          <span className="as-sub"><CompanyChip name={d.company_name} color={d.company_color} code={d.company_code} /></span>
        )}
        {f.lost && <span className="as-chip" data-tone="critical">Lost</span>}
        {f.incomplete && <span className="as-chip" data-tone="warning">To complete</span>}
      </td>
      <td data-label="With">
        {d.holder_name || <span className="as-quiet">Nobody</span>}
      </td>
      <td data-label="Signed in">
        {who ? (
          <>
            <span data-differs={String(f.holder_differs)}>{who}</span>
            {f.holder_differs && <span className="as-chip" data-tone="warning">Not the holder</span>}
            {d.signed_in && !d.signed_in.user_id && <span className="as-sub">Not a person Jarvis knows</span>}
          </>
        ) : <span className="as-quiet">{d.last_heartbeat_at ? 'Nobody' : '—'}</span>}
        {d.users_recent && d.users_recent.length > 1 && (
          <span className="as-sub" title={d.users_recent.map((u) => u.upn || u.account).join(', ')}>
            {`${d.users_recent.length} people in 14 days`}
          </span>
        )}
      </td>
      <td data-label="Last seen">
        <span className="as-chip" data-tone={f.stale ? 'warning' : 'success'}>{f.stale ? 'Not reporting' : 'Reporting'}</span>
        <span className="as-sub">{d.last_heartbeat_at ? `report ${ago(d.last_heartbeat_at)}` : 'never reported'}{d.checked_in_at ? ` · checked in ${ago(d.checked_in_at)}` : ''}</span>
      </td>
      <td data-label="OS">
        {d.os || <span className="as-quiet">—</span>}
        {f.needs_patching && (
          <>
            <span className="as-chip" data-tone="warning">Needs patching</span>
            <span className="as-sub" data-testid="patching-reasons">{(d.patching_reasons || []).join('; ')}</span>
          </>
        )}
      </td>
      <td data-label="Encryption">
        <span className="as-chip" data-tone={encryptionTone(d.encryption)}>{encryptionWord(d.encryption)}</span>
      </td>
      <td data-label="Disk">
        {d.disk_used_pct === null || d.disk_used_pct === undefined
          ? <span className="as-quiet">—</span>
          : <span className="as-chip" data-tone={diskTone(d.disk_used_pct)}>{`${d.disk_used_pct}% full`}</span>}
      </td>
      <td data-label="Agent">
        {d.agent ? `${d.agent} ${d.agent_version || ''}`.trim() : <span className="as-quiet">Stopped</span>}
      </td>
      <td className="as-dev-actions" onClick={(e) => e.stopPropagation()}>
        {d.can_make_holder && (
          <button type="button" className="btn btn-secondary btn-small" onClick={onMakeHolder}>
            {`Make ${d.signed_in.name} the holder`}
          </button>
        )}
        {f.incomplete && d.can_change && (
          <Link className="btn btn-plain btn-small"
            state={cameFrom('Devices')}
            to={`/assets/${d.id}?edit=1${d.signed_in?.user_id && !d.assigned_user_id ? `&holder=${d.signed_in.user_id}` : ''}`}>
            Complete this device
          </Link>
        )}
      </td>
    </tr>
  );
}
